import { Autocomplete, TextField } from '@mui/material';
import { Controller, Path, PathValue } from 'react-hook-form';
import { BaseControlledInputProps } from '../BasicControlledInputProps';
import FormFieldWrappers from '../FormFieldWrappers';
import { ReactNode } from 'react';
import { useFormTracking, useFormOptionTracking } from '../hooks';

export type SelectOption<VALUE> = {
  label: ReactNode;
  value: VALUE;
  notSelectable?: boolean;
};

type MultiSelectProps<VALUE> = {
  options: SelectOption<VALUE>[];
  /**
   * Style properties
   */
  style?: React.CSSProperties;
};

type ArrayElement<T> = T extends (infer U)[] ? U : never;

export type ControlledMultiSelectProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
> = Omit<
  BaseControlledInputProps<FORM_TYPE, FIELD_NAME>,
  'onBlurCallback' | 'onFocusCallback'
> &
  MultiSelectProps<ArrayElement<PathValue<FORM_TYPE, FIELD_NAME>>>;

export const ControlledMultiSelect = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
>({
  name,
  label,
  disabled,
  control,
  options,
  style,
  trackingName,
  sensitive = true,
}: ControlledMultiSelectProps<FORM_TYPE, FIELD_NAME>) => {
  // Auto-generate tracking props if trackingName is provided
  const trackingProps = useFormTracking(trackingName);
  const { generateOptionTracking } = useFormOptionTracking(
    trackingName,
    sensitive,
    options
  );

  return (
    <FormFieldWrappers.FieldWrapper>
      <FormFieldWrappers.InputWrapper>
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const handleChange = (
              _,
              data: SelectOption<
                ArrayElement<PathValue<FORM_TYPE, FIELD_NAME>>
              >[]
            ) => {
              onChange(data.map(item => item.value));
            };
            const selectedValue = options.filter(
              option => Array.isArray(value) && value.includes(option.value)
            );
            return (
              <Autocomplete
                {...trackingProps}
                style={style}
                defaultValue={[]}
                value={selectedValue}
                options={options}
                onChange={handleChange}
                multiple={true}
                disabled={disabled}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <>{params.InputProps.startAdornment}</>,
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    {...generateOptionTracking(
                      option.value,
                      String(option.label)
                    )}
                  >
                    {option.label}
                  </li>
                )}
              />
            );
          }}
        />
      </FormFieldWrappers.InputWrapper>
    </FormFieldWrappers.FieldWrapper>
  );
};
